/**
 * Section to Profile Data Mapping
 *
 * Defines which profile fields are used to generate each proposal section,
 * what gaps might exist, and how to improve them.
 */

export interface ProfileFieldMapping {
  field: string;           // Profile field/table name
  label: string;           // Human-readable label
  description: string;     // What this data is used for
  profilePath: string;     // Path in the profile edit UI (for navigation)
  required: boolean;       // Is this critical for the section?
  checkFn?: (profile: any) => boolean; // Function to check if data exists
}

export interface SectionProfileMapping {
  sectionType: string;
  sectionTitle: string;
  description: string;
  profileSources: ProfileFieldMapping[];
  tips: string[];
  bestPractices: string[];
}

// Define the mapping for each proposal section
export const SECTION_PROFILE_MAPPINGS: Record<string, SectionProfileMapping> = {
  'pricing': {
    sectionType: 'pricing',
    sectionTitle: 'Pricing & Cost Strategy',
    description: 'Your pricing section is built from labor rates, indirect costs, and past contract values in your profile.',
    profileSources: [
      {
        field: 'enhanced_pricing_data',
        label: 'Labor Categories & Rates',
        description: 'Hourly rates, loaded rates, and labor category definitions',
        profilePath: '/profile#pricing',
        required: true,
        checkFn: (profile) => profile?.pricingData?.length > 0 || profile?.laborCategories?.length > 0,
      },
      {
        field: 'enhanced_pricing_data.indirect_rates',
        label: 'Indirect Rates (Fringe, Overhead, G&A)',
        description: 'Burden rates applied to direct labor costs',
        profilePath: '/profile#pricing',
        required: true,
        checkFn: (profile) => profile?.indirectRates?.length > 0,
      },
      {
        field: 'past_performance',
        label: 'Past Contract Values',
        description: 'Historical contract values help benchmark pricing',
        profilePath: '/profile#performance',
        required: false,
        checkFn: (profile) => profile?.pastPerformance?.some((pp: any) => pp.contract_value),
      },
      {
        field: 'profiles.gsa_schedule',
        label: 'GSA Schedule Rates',
        description: 'Pre-negotiated government rates (if applicable)',
        profilePath: '/profile#pricing',
        required: false,
        checkFn: (profile) => !!profile?.gsa_schedule_number,
      },
      {
        field: 'enhanced_pricing_data.dcaa_audit',
        label: 'DCAA Audit Status',
        description: 'Audit status strengthens rate credibility',
        profilePath: '/profile#pricing',
        required: false,
        checkFn: (profile) => !!profile?.dcaa_audit_status,
      },
    ],
    tips: [
      'Use GSA Schedule rates as a benchmark if you have them',
      'Document your rate methodology clearly - evaluators want to see the math',
      'Include burden/wrap rates with clear breakdowns',
      'Show competitive positioning vs. industry standards',
      'If DCAA audited, highlight this - it builds credibility',
    ],
    bestPractices: [
      'Labor rates should reflect actual costs plus reasonable margin',
      'Indirect rates should be supportable with accounting records',
      'Include escalation factors for multi-year contracts',
      'Consider including optional CLINs for flexibility',
      'Provide clear justification for each cost element',
    ],
  },

  'cover_letter': {
    sectionType: 'cover_letter',
    sectionTitle: 'Cover Letter',
    description: 'Your cover letter draws from company information, key contacts, and your value proposition.',
    profileSources: [
      {
        field: 'profiles.company_name',
        label: 'Company Name & Address',
        description: 'Official company information for letterhead',
        profilePath: '/profile#company',
        required: true,
        checkFn: (profile) => !!profile?.company_name,
      },
      {
        field: 'profiles.value_proposition',
        label: 'Value Proposition',
        description: 'Your unique selling points and differentiators',
        profilePath: '/profile#company',
        required: true,
        checkFn: (profile) => !!profile?.value_proposition && profile.value_proposition.length > 50,
      },
      {
        field: 'key_personnel',
        label: 'Authorized Signatory',
        description: 'Person authorized to sign proposals',
        profilePath: '/profile#personnel',
        required: true,
        checkFn: (profile) => profile?.keyPersonnel?.some((p: any) => p.is_authorized_signatory),
      },
      {
        field: 'profiles.contact_info',
        label: 'Contact Information',
        description: 'Phone, email, and website for the letter',
        profilePath: '/profile#company',
        required: true,
        checkFn: (profile) => !!profile?.contact_email && !!profile?.contact_phone,
      },
    ],
    tips: [
      'Keep the opening paragraph focused on the customer\'s needs, not your company',
      'Reference the specific solicitation number and title',
      'Mention 1-2 key discriminators that set you apart',
      'End with a clear call to action and contact information',
    ],
    bestPractices: [
      'Address the letter to the correct contracting officer',
      'Keep it to one page maximum',
      'Use professional, confident tone without being arrogant',
      'Proofread carefully - this is your first impression',
    ],
  },

  'technical_approach': {
    sectionType: 'technical_approach',
    sectionTitle: 'Technical Approach',
    description: 'Your technical approach leverages your capabilities, technologies, and past project experience.',
    profileSources: [
      {
        field: 'capabilities',
        label: 'Core Capabilities',
        description: 'Technical competencies and service offerings',
        profilePath: '/profile#capabilities',
        required: true,
        checkFn: (profile) => profile?.capabilities?.length > 0,
      },
      {
        field: 'profiles.key_technologies',
        label: 'Technologies & Tools',
        description: 'Software, platforms, and methodologies you use',
        profilePath: '/profile#capabilities',
        required: true,
        checkFn: (profile) => profile?.key_technologies?.length > 0,
      },
      {
        field: 'past_performance',
        label: 'Similar Project Experience',
        description: 'Past projects that demonstrate technical capability',
        profilePath: '/profile#performance',
        required: true,
        checkFn: (profile) => profile?.pastPerformance?.length > 0,
      },
      {
        field: 'profiles.certifications',
        label: 'Technical Certifications',
        description: 'ISO, CMMI, or other quality certifications',
        profilePath: '/profile#company',
        required: false,
        checkFn: (profile) => profile?.certifications?.length > 0,
      },
    ],
    tips: [
      'Address each requirement from the SOW explicitly',
      'Use the government\'s terminology from the solicitation',
      'Include specific tools and methodologies, not generic descriptions',
      'Show understanding of the agency\'s mission and challenges',
    ],
    bestPractices: [
      'Create a compliance matrix mapping requirements to your approach',
      'Include graphics and diagrams where helpful',
      'Quantify benefits where possible (e.g., "reduces processing time by 40%")',
      'Reference similar past work to prove you can deliver',
    ],
  },

  'management_approach': {
    sectionType: 'management_approach',
    sectionTitle: 'Management Approach',
    description: 'Your management approach showcases your organizational structure and project management capabilities.',
    profileSources: [
      {
        field: 'key_personnel',
        label: 'Key Management Personnel',
        description: 'Project managers and leadership team',
        profilePath: '/profile#personnel',
        required: true,
        checkFn: (profile) => profile?.keyPersonnel?.filter((p: any) =>
          p.title?.toLowerCase().includes('manager') ||
          p.title?.toLowerCase().includes('director') ||
          p.title?.toLowerCase().includes('lead')
        ).length > 0,
      },
      {
        field: 'profiles.quality_certifications',
        label: 'Quality Management Certifications',
        description: 'ISO 9001, CMMI, or similar certifications',
        profilePath: '/profile#company',
        required: false,
        checkFn: (profile) => profile?.certifications?.some((c: string) =>
          c.toLowerCase().includes('iso') || c.toLowerCase().includes('cmmi')
        ),
      },
      {
        field: 'past_performance',
        label: 'Management Track Record',
        description: 'Past projects demonstrating successful management',
        profilePath: '/profile#performance',
        required: true,
        checkFn: (profile) => profile?.pastPerformance?.length > 0,
      },
    ],
    tips: [
      'Show clear lines of authority and communication',
      'Include an org chart even if simple',
      'Define roles and responsibilities clearly',
      'Address risk management proactively',
    ],
    bestPractices: [
      'Tailor the org chart to the contract scope',
      'Show how you\'ll communicate with the government PM',
      'Include quality control and assurance processes',
      'Define escalation procedures for issues',
    ],
  },

  'past_performance': {
    sectionType: 'past_performance',
    sectionTitle: 'Past Performance',
    description: 'Your past performance section draws directly from your documented contract history.',
    profileSources: [
      {
        field: 'past_performance',
        label: 'Past Performance Records',
        description: 'Documented contracts with references',
        profilePath: '/profile#performance',
        required: true,
        checkFn: (profile) => profile?.pastPerformance?.length >= 3,
      },
      {
        field: 'past_performance.references',
        label: 'Client References',
        description: 'Contact information for past clients',
        profilePath: '/profile#performance',
        required: true,
        checkFn: (profile) => profile?.pastPerformance?.some((pp: any) => pp.reference_name && pp.reference_email),
      },
      {
        field: 'past_performance.contract_numbers',
        label: 'Contract Numbers',
        description: 'Official contract identifiers for verification',
        profilePath: '/profile#performance',
        required: false,
        checkFn: (profile) => profile?.pastPerformance?.some((pp: any) => pp.contract_number),
      },
      {
        field: 'past_performance.cpars_ratings',
        label: 'CPARS Ratings',
        description: 'Official government performance ratings',
        profilePath: '/profile#performance',
        required: false,
        checkFn: (profile) => profile?.pastPerformance?.some((pp: any) => pp.cpars_rating),
      },
    ],
    tips: [
      'Select past performance most relevant to the current requirement',
      'Emphasize similar scope, size, and complexity',
      'Include quantifiable achievements and metrics',
      'Ensure your references are current and will respond positively',
    ],
    bestPractices: [
      'Contact references before submitting to ensure availability',
      'Include at least 3 relevant past performance examples',
      'Highlight awards, commendations, or option exercises',
      'Address any negative CPARS proactively if applicable',
    ],
  },

  'key_personnel': {
    sectionType: 'key_personnel',
    sectionTitle: 'Key Personnel',
    description: 'Your key personnel section showcases the qualifications of your proposed team members.',
    profileSources: [
      {
        field: 'key_personnel',
        label: 'Personnel Records',
        description: 'Team member profiles with qualifications',
        profilePath: '/profile#personnel',
        required: true,
        checkFn: (profile) => profile?.keyPersonnel?.length >= 2,
      },
      {
        field: 'key_personnel.certifications',
        label: 'Professional Certifications',
        description: 'PMP, CISSP, AWS, etc.',
        profilePath: '/profile#personnel',
        required: true,
        checkFn: (profile) => profile?.keyPersonnel?.some((p: any) => p.certifications?.length > 0),
      },
      {
        field: 'key_personnel.education',
        label: 'Education & Degrees',
        description: 'Academic qualifications',
        profilePath: '/profile#personnel',
        required: true,
        checkFn: (profile) => profile?.keyPersonnel?.some((p: any) => p.education),
      },
      {
        field: 'key_personnel.security_clearance',
        label: 'Security Clearances',
        description: 'Active clearance levels',
        profilePath: '/profile#personnel',
        required: false,
        checkFn: (profile) => profile?.keyPersonnel?.some((p: any) => p.security_clearance),
      },
      {
        field: 'key_personnel.resume_url',
        label: 'Resumes',
        description: 'Full resumes for key personnel',
        profilePath: '/profile#personnel',
        required: false,
        checkFn: (profile) => profile?.keyPersonnel?.some((p: any) => p.resume_url),
      },
    ],
    tips: [
      'Match personnel qualifications to solicitation requirements',
      'Highlight relevant certifications prominently',
      'Include years of experience with specific technologies/domains',
      'Show continuity - how long team members have worked together',
    ],
    bestPractices: [
      'Ensure proposed personnel are actually available for the contract',
      'Include letters of commitment for consultants/subcontractors',
      'Provide detailed role descriptions for each position',
      'Address any gaps in qualifications proactively',
    ],
  },

  'compliance': {
    sectionType: 'compliance',
    sectionTitle: 'FAR/DFARS Compliance',
    description: 'Your compliance section is built from your certifications, registrations, and compliance status.',
    profileSources: [
      {
        field: 'profiles.sam_uei',
        label: 'SAM.gov Registration (UEI)',
        description: 'Active SAM.gov registration',
        profilePath: '/profile#company',
        required: true,
        checkFn: (profile) => !!profile?.sam_uei,
      },
      {
        field: 'profiles.cage_code',
        label: 'CAGE Code',
        description: 'Commercial and Government Entity code',
        profilePath: '/profile#company',
        required: true,
        checkFn: (profile) => !!profile?.cage_code,
      },
      {
        field: 'profiles.certifications',
        label: 'Business Certifications',
        description: '8(a), HUBZone, SDVOSB, WOSB, etc.',
        profilePath: '/profile#company',
        required: false,
        checkFn: (profile) => profile?.certifications?.length > 0,
      },
      {
        field: 'compliance_documents',
        label: 'Compliance Documents',
        description: 'Reps & Certs, insurance certificates',
        profilePath: '/profile#compliance',
        required: false,
        checkFn: (profile) => profile?.complianceDocuments?.length > 0,
      },
      {
        field: 'profiles.security_clearance',
        label: 'Facility Clearance',
        description: 'Facility security clearance level',
        profilePath: '/profile#company',
        required: false,
        checkFn: (profile) => !!profile?.facility_clearance,
      },
    ],
    tips: [
      'Ensure SAM.gov registration is current (renews annually)',
      'Keep all certifications and insurance current',
      'Review FAR/DFARS clauses in solicitation for specific requirements',
      'Address cybersecurity requirements (NIST 800-171, CMMC) if applicable',
    ],
    bestPractices: [
      'Create a compliance checklist for each solicitation',
      'Maintain a library of standard certifications and representations',
      'Update SAM.gov immediately when company info changes',
      'Keep copies of all certifications readily accessible',
    ],
  },
};

/**
 * Analyze profile gaps for a specific section
 */
export function analyzeProfileGaps(
  sectionType: string,
  profile: any
): {
  score: number;
  totalFields: number;
  missingRequired: ProfileFieldMapping[];
  missingOptional: ProfileFieldMapping[];
  present: ProfileFieldMapping[];
} {
  const mapping = SECTION_PROFILE_MAPPINGS[sectionType];
  if (!mapping) {
    return { score: 0, totalFields: 0, missingRequired: [], missingOptional: [], present: [] };
  }

  const missingRequired: ProfileFieldMapping[] = [];
  const missingOptional: ProfileFieldMapping[] = [];
  const present: ProfileFieldMapping[] = [];

  for (const source of mapping.profileSources) {
    const hasData = source.checkFn ? source.checkFn(profile) : false;

    if (hasData) {
      present.push(source);
    } else if (source.required) {
      missingRequired.push(source);
    } else {
      missingOptional.push(source);
    }
  }

  const totalFields = mapping.profileSources.length;
  const presentCount = present.length;
  const score = Math.round((presentCount / totalFields) * 100);

  return {
    score,
    totalFields,
    missingRequired,
    missingOptional,
    present,
  };
}

/**
 * Get the section type key from various formats
 */
export function normalizeSectionType(sectionType: string): string {
  const normalized = sectionType
    .toLowerCase()
    .replace(/[^a-z_]/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_|_$/g, '');

  const typeMapping: Record<string, string> = {
    'coverletter': 'cover_letter',
    'cover_letter': 'cover_letter',
    'technical_approach': 'technical_approach',
    'technicalapproach': 'technical_approach',
    'management_approach': 'management_approach',
    'managementapproach': 'management_approach',
    'past_performance': 'past_performance',
    'pastperformance': 'past_performance',
    'key_personnel': 'key_personnel',
    'keypersonnel': 'key_personnel',
    'pricing': 'pricing',
    'pricing_cost_strategy': 'pricing',
    'pricing___cost_strategy': 'pricing',
    'compliance': 'compliance',
    'far_dfars_compliance': 'compliance',
    'fardfars_compliance': 'compliance',
  };

  return typeMapping[normalized] || normalized;
}
